/**
 * eventUtils.js — pure helpers for Event Pre-Orders.
 *
 * Deliberately free of React/Firestore imports so it can be unit-tested in
 * isolation (see events.test.js): id generation, field/item factories, the
 * order-accepting lifecycle check, submission validation, kitchen aggregation,
 * and CSV export.
 */
import { FIELD_TYPE_MAP, fieldIsInput, fieldIsMulti } from './fieldTypes';

const ID_CHARS = 'abcdefghijklmnopqrstuvwxyz0123456789';

/** Short, URL-safe, lowercase base36 id. Used for event doc ids, fields, items. */
export function genId(len = 8) {
  const cryptoObj = typeof crypto !== 'undefined' ? crypto : undefined;
  let out = '';
  if (cryptoObj && typeof cryptoObj.getRandomValues === 'function') {
    const arr = new Uint32Array(len);
    cryptoObj.getRandomValues(arr);
    for (let i = 0; i < len; i++) out += ID_CHARS[arr[i] % ID_CHARS.length];
  } else {
    for (let i = 0; i < len; i++) out += ID_CHARS[Math.floor(Math.random() * ID_CHARS.length)];
  }
  return out;
}

// ── Factories ────────────────────────────────────────────────────────────────

/** A fresh custom form field of the given type. */
export function newField(type = 'short_text') {
  const def = FIELD_TYPE_MAP[type] || FIELD_TYPE_MAP.short_text;
  const field = { id: genId(6), type, label: '', required: false, placeholder: '', helpText: '' };
  if (def.hasOptions) field.options = ['Option 1', 'Option 2'];
  return field;
}

/** A fresh orderable menu item. */
export function newItem() {
  return { id: genId(6), name: '', description: '', price: '', category: '', maxQty: '' };
}

/** Default settings block for a brand-new event. */
export function defaultSettings() {
  return {
    requireName: true,
    requirePhone: false,
    requireEmail: false,
    oneOrderPerDevice: true,
    allowInstructions: true,
    showPrices: true,
    payNote: '',
    capacity: null,
  };
}

/** A fresh, empty reusable menu for the library. */
export function blankMenu() {
  return { name: '', items: [] };
}

/** Copy saved-menu items into an event with fresh unique ids (avoids clashes). */
export function importMenuItems(items) {
  return (items || []).map((it) => ({ ...it, id: genId(6) }));
}

/** A blank event ready for the builder. */
export function blankEvent() {
  return {
    title: '',
    description: '',
    bannerColor: '#f59e0b',
    status: 'draft',
    deadline: null,
    settings: defaultSettings(),
    menuLink: '',
    menuItems: [],
    fields: [],
  };
}

/** Ensure a user-typed menu URL has a scheme so it works as an href. */
export function normalizeUrl(url) {
  const u = String(url || '').trim();
  if (!u) return '';
  return /^https?:\/\//i.test(u) ? u : `https://${u}`;
}

// ── Time helpers (Firestore Timestamp | ms number | ISO string tolerant) ──────

export function toMs(ts) {
  if (ts == null) return null;
  if (typeof ts === 'number') return ts;
  if (typeof ts === 'object') {
    if (typeof ts.toMillis === 'function') return ts.toMillis();
    if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  }
  const parsed = Date.parse(ts);
  return Number.isNaN(parsed) ? null : parsed;
}

export function formatDateTime(ts) {
  const ms = toMs(ts);
  if (!ms) return '';
  return new Date(ms).toLocaleString([], {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

/** Compact "just now / 5m / 3h / 2d ago" relative time for dashboards. */
export function timeAgo(ts) {
  const ms = toMs(ts);
  if (!ms) return '';
  const sec = Math.round((Date.now() - ms) / 1000);
  if (sec < 45) return 'just now';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(ms).toLocaleDateString([], { month: 'short', day: 'numeric' });
}

/** Format a number as USD. */
export function formatMoney(n) {
  return `$${(Number(n) || 0).toFixed(2)}`;
}

/** Running subtotal for the picked quantities (0 when no prices set). */
export function orderTotal(event, quantities) {
  return (event.menuItems || []).reduce((sum, mi) => {
    const qty = Number(quantities?.[mi.id]) || 0;
    const price = Number(mi.price) || 0;
    return sum + qty * price;
  }, 0);
}

// ── Lifecycle ────────────────────────────────────────────────────────────────

export const STATUS_META = {
  draft:    { label: 'Draft',    color: '#94a3b8', icon: '📄' },
  open:     { label: 'Open',     color: '#22c55e', icon: '🟢' },
  closed:   { label: 'Closed',   color: '#ef4444', icon: '🔴' },
  archived: { label: 'Archived', color: '#64748b', icon: '🗄️' },
};

// ── Lifecycle history (audit timeline) ───────────────────────────────────────

export const HISTORY_META = {
  created:  { icon: '✨', label: 'Created' },
  edited:   { icon: '✏️', label: 'Edited' },
  opened:   { icon: '🟢', label: 'Opened for orders' },
  closed:   { icon: '🔴', label: 'Closed' },
  archived: { icon: '🗄️', label: 'Archived' },
  restored: { icon: '♻️', label: 'Restored' },
  placed:   { icon: '🧾', label: 'Order placed' }, // synthetic order-timeline entry
};

/** Normalize the current user into a compact actor for history entries. */
export function actorFrom(user) {
  return { uid: user?.uid || '', name: user?.displayName || user?.email || 'Someone' };
}

/** One immutable audit-timeline entry: what happened, when, and by whom. */
export function historyEntry(action, actor) {
  return { action, at: Date.now(), by: actor?.name || '', byUid: actor?.uid || '' };
}

/** An order-timeline entry for an attendee edit (carries a human change summary). */
export function orderEditEntry(name, summary) {
  return { action: 'edited', at: Date.now(), by: name || '', summary: summary || 'updated' };
}

/**
 * Human-readable summary of what changed between two order snapshots — spells out
 * the actual before→after values, not just which field changed, e.g.
 * `Biryani 2→3 · +Samosa ×1 · note: "no onions" → "extra spicy" · phone added: "…"`.
 * Long text is truncated. Pure/testable.
 * Snapshots: { quantities, orderText, name, phone, email, answers }.
 */
export function diffOrder(event, prev, next) {
  const parts = [];
  const pq = (prev && prev.quantities) || {};
  const nq = (next && next.quantities) || {};
  for (const mi of event.menuItems || []) {
    const o = Number(pq[mi.id]) || 0;
    const n = Number(nq[mi.id]) || 0;
    if (o === n) continue;
    if (o === 0) parts.push(`+${mi.name} ×${n}`);
    else if (n === 0) parts.push(`−${mi.name}`);
    else parts.push(`${mi.name} ${o}→${n}`);
  }
  const g = (obj, k) => String((obj && obj[k]) || '').trim();
  const trunc = (s) => (s.length > 40 ? `${s.slice(0, 39)}…` : s);
  const quo = (s) => `"${trunc(s)}"`;
  // Emit "<label>: "old" → "new"" (or added/removed) for a changed text field.
  const change = (label, a, b) => {
    if (a === b) return;
    if (!a) parts.push(`${label} added: ${quo(b)}`);
    else if (!b) parts.push(`${label} removed`);
    else parts.push(`${label}: ${quo(a)} → ${quo(b)}`);
  };
  const orderLabel = (event.menuItems && event.menuItems.length) ? 'note' : 'order';
  change(orderLabel, g(prev, 'orderText'), g(next, 'orderText'));
  change('name', g(prev, 'name'), g(next, 'name'));
  change('phone', g(prev, 'phone'), g(next, 'phone'));
  change('email', g(prev, 'email'), g(next, 'email'));
  // Per-question answer changes, labelled by the question.
  const pa = (prev && prev.answers) || {};
  const na = (next && next.answers) || {};
  const fmtA = (v) => (Array.isArray(v) ? v.join(', ') : String(v == null ? '' : v)).trim();
  for (const f of event.fields || []) {
    if (!fieldIsInput(f.type)) continue;
    change(f.label || 'answer', fmtA(pa[f.id]), fmtA(na[f.id]));
  }
  return parts.join(' · ');
}

/**
 * Parse a stored change `summary` (from diffOrder) back into typed tokens so the
 * timeline can render each change clearly + color-coded. Pure/testable. Old or
 * unrecognized summaries fall through as a single 'note'. "updated" → no tokens.
 * Kinds: add | remove | change | note.
 */
export function splitChanges(summary) {
  const s = String(summary || '').trim();
  if (!s || s === 'updated') return [];
  return s.split(' · ').map((raw) => {
    const t = raw.trim();
    if (!t) return null;
    let m = t.match(/^(.+?):\s*"(.*)"\s*→\s*"(.*)"$/);              // field: "a" → "b"
    if (m) return { kind: 'change', label: m[1], from: m[2], to: m[3] };
    m = t.match(/^(.+?) added:\s*"(.*)"$/);                          // field added: "b"
    if (m) return { kind: 'add', label: m[1], to: m[2] };
    m = t.match(/^(.+?) removed$/);                                  // field removed
    if (m) return { kind: 'remove', label: m[1] };
    m = t.match(/^(.+?)\s(\d+)→(\d+)$/);                             // Item 2→3
    if (m) return { kind: 'change', label: m[1], from: m[2], to: m[3] };
    if (/^\+/.test(t)) return { kind: 'add', text: t.replace(/^\+\s*/, '') };        // +Item ×2
    if (/^[−-]/.test(t)) return { kind: 'remove', text: t.replace(/^[−-]\s*/, '') }; // −Item
    return { kind: 'note', text: t };
  }).filter(Boolean);
}

/** Map a target status to the history action it represents. */
export function statusAction(status) {
  return { open: 'opened', closed: 'closed', archived: 'archived', draft: 'restored' }[status] || 'edited';
}

/** Is the event currently accepting pre-orders? (status + optional deadline) */
export function isAcceptingOrders(event, now = Date.now()) {
  if (!event || event.status !== 'open') return false;
  const deadlineMs = toMs(event.deadline);
  if (deadlineMs && now > deadlineMs) return false;
  return true;
}

/** Why a form isn't accepting orders — drives which "closed" screen to show. */
export function closedReason(event, now = Date.now()) {
  if (!event) return 'not_found';
  if (event.status === 'draft') return 'not_open';
  if (event.status === 'archived') return 'closed';
  if (event.status === 'closed') return 'closed';
  const deadlineMs = toMs(event.deadline);
  if (deadlineMs && now > deadlineMs) return 'deadline';
  return null; // accepting
}

// ── Validation ───────────────────────────────────────────────────────────────

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Phone: an optional leading +, then only digits, spaces, dashes, dots, parentheses.
const PHONE_SHAPE_RE = /^\+?[\d\s().-]+$/;
const HAS_LETTER_RE = /\p{L}/u;     // at least one letter (any script) — reject pure numbers/symbols
const LOOKS_LIKE_URL_RE = /(https?:\/\/|www\.)/i;

/** Basic email check (also used for custom "email" fields). */
export function isValidEmail(email) {
  return EMAIL_RE.test(String(email || '').trim());
}

/**
 * Basic, international-friendly phone check: allowed characters + a sane digit
 * count (7–15, per E.164). Deliberately permissive — not a carrier lookup.
 */
export function isValidPhone(phone) {
  const s = String(phone || '').trim();
  if (!s || !PHONE_SHAPE_RE.test(s)) return false;
  const digits = s.replace(/\D/g, '');
  // With an explicit country code (+…): country code + a ~10-digit national number.
  // Without one: a national number must be exactly 10 digits (US/CA/India) — this
  // is what normalizePhone/phoneClaimKey assume, so 9-digit numbers are rejected.
  if (s.startsWith('+')) return digits.length >= 11 && digits.length <= 15;
  return digits.length === 10;
}

/**
 * Basic name check — intentionally light (names are diverse): 2–60 chars, at
 * least one letter, and not a pasted URL (spam guard).
 */
export function isValidName(name) {
  const s = String(name || '').trim();
  if (s.length < 2 || s.length > 60) return false;
  if (!HAS_LETTER_RE.test(s)) return false;
  if (LOOKS_LIKE_URL_RE.test(s)) return false;
  return true;
}

/**
 * Validate a public order submission against the event's config.
 * Returns a map of { fieldKey: message }. Empty map means valid.
 */
export function validateOrder(event, order) {
  const errors = {};
  const s = (event && event.settings) || {};
  const name = String(order.respondentName || '').trim();
  const phone = String(order.respondentPhone || '').trim();
  const email = String(order.respondentEmail || '').trim();

  // Name — required check, then a light format check when provided.
  if (!name) {
    if (s.requireName !== false) errors.respondentName = 'Please enter your name';
  } else if (!isValidName(name)) {
    errors.respondentName = 'Please enter a valid name';
  }

  // Phone — required check, then format (allows +, spaces, dashes, parentheses).
  if (!phone) {
    if (s.requirePhone) errors.respondentPhone = 'Please enter your phone number';
  } else if (!isValidPhone(phone)) {
    errors.respondentPhone = 'Enter a valid phone number, e.g. (555) 123-4567';
  }

  // Email — required check, then format.
  if (!email) {
    if (s.requireEmail) errors.respondentEmail = 'Please enter your email';
  } else if (!isValidEmail(email)) {
    errors.respondentEmail = 'Enter a valid email, e.g. name@example.com';
  }

  const items = (event && event.menuItems) || [];
  if (items.length > 0) {
    const totalQty = (order.items || []).reduce((sum, it) => sum + (Number(it.qty) || 0), 0);
    if (totalQty <= 0) errors.items = 'Please select at least one item';
  } else if (event && event.menuLink) {
    // Link-only menu: capture the order as free text instead of an item picker.
    if (!String(order.orderText || '').trim()) errors.orderText = 'Please enter your order';
  }

  // Custom fields — required check, then per-type format validation.
  for (const f of (event && event.fields) || []) {
    if (!fieldIsInput(f.type)) continue;
    const v = order.answers ? order.answers[f.id] : undefined;
    const empty = v == null || (Array.isArray(v) ? v.length === 0 : String(v).trim() === '');
    const key = `field_${f.id}`;
    if (empty) {
      if (f.required) errors[key] = `${f.label || 'This field'} is required`;
      continue;
    }
    const sv = String(v).trim();
    if (f.type === 'email' && !isValidEmail(sv)) errors[key] = 'Please enter a valid email';
    else if (f.type === 'phone' && !isValidPhone(sv)) errors[key] = 'Please enter a valid phone number';
    else if (f.type === 'number' && !Number.isFinite(Number(sv))) errors[key] = 'Please enter a number';
  }
  return errors;
}

/**
 * Build the clean eventOrders payload from raw form state:
 * strips zero-qty items and empty answers so nothing junk is written.
 */
// ── Phone-based order lookup (cross-device "find my order") ───────────────────

/** Reduce a phone to comparable digits (drops formatting; keeps last 10 if longer). */
export function normalizePhone(phone) {
  const digits = String(phone || '').replace(/\D+/g, '');
  return digits.length > 10 ? digits.slice(-10) : digits;
}

/** Tiny stable string hash → base36, so a claim id never embeds the raw phone. */
function hash36(str) {
  let h = 5381;
  for (let i = 0; i < str.length; i += 1) h = ((h * 33) ^ str.charCodeAt(i)) >>> 0;
  return h.toString(36);
}

/**
 * Deterministic id of the public claim doc for (event, phone). A customer can
 * recompute it from their own phone to find their order on ANY device — the
 * capability that gates cross-device lookup. Returns '' when there's no phone.
 */
export function phoneClaimKey(eventId, phone) {
  const p = normalizePhone(phone);
  if (!eventId || !p) return '';
  return `${eventId}_${hash36(p)}`;
}

/** Stored order items [{id,qty}] → a {id: qty} map (for recaps + merging). */
export function itemsToQuantities(items) {
  const q = {};
  (items || []).forEach((it) => { if (it && it.id) q[it.id] = Number(it.qty) || 0; });
  return q;
}

/** Merge newly-picked quantities INTO an existing order's items → summed {id: qty}. */
export function mergeQuantities(existingItems, addQuantities) {
  const merged = itemsToQuantities(existingItems);
  Object.entries(addQuantities || {}).forEach(([id, qty]) => {
    const n = Number(qty) || 0;
    if (n > 0) merged[id] = (Number(merged[id]) || 0) + n;
  });
  return merged;
}

export function buildOrderPayload(event, order, deviceId) {
  const items = (event.menuItems || [])
    .map((mi) => {
      const qty = Number(order.quantities?.[mi.id]) || 0;
      if (qty <= 0) return null;
      const item = { id: mi.id, name: mi.name, qty };
      if (mi.price != null && mi.price !== '') item.price = Number(mi.price) || 0;
      return item;
    })
    .filter(Boolean);

  const answers = {};
  for (const f of event.fields || []) {
    if (!fieldIsInput(f.type)) continue;
    const v = order.answers ? order.answers[f.id] : undefined;
    const empty = v == null || (Array.isArray(v) ? v.length === 0 : String(v).trim() === '');
    if (!empty) answers[f.id] = fieldIsMulti(f.type) ? v : v;
  }

  const payload = {
    eventId: event.id,
    eventTitle: event.title || '',
    respondentName: String(order.respondentName || '').trim(),
    items,
    answers,
    deviceId: deviceId || '',
  };
  const phone = String(order.respondentPhone || '').trim();
  const email = String(order.respondentEmail || '').trim();
  const instructions = String(order.instructions || '').trim();
  const orderText = String(order.orderText || '').trim();
  if (phone) payload.respondentPhone = phone;
  if (email) payload.respondentEmail = email;
  if (orderText) payload.orderText = orderText;
  if (instructions) payload.instructions = instructions;
  return payload;
}

// ── Aggregation (kitchen prep summary) ───────────────────────────────────────

/** Total quantity per item across all orders, sorted busiest-first. */
export function aggregateItems(orders) {
  const map = new Map();
  for (const o of orders || []) {
    for (const it of o.items || []) {
      const key = it.id || it.name;
      const prev = map.get(key) || { id: it.id, name: it.name, totalQty: 0 };
      prev.totalQty += Number(it.qty) || 0;
      if (it.name) prev.name = it.name;
      map.set(key, prev);
    }
  }
  return [...map.values()]
    .filter((x) => x.totalQty > 0)
    .sort((a, b) => b.totalQty - a.totalQty || String(a.name).localeCompare(String(b.name)));
}

/** Re-sort an aggregated item list: 'name' = A→Z (prep-friendly), else busiest-first. */
export function sortItemsBy(items, mode) {
  const byName = (a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' });
  const arr = [...(items || [])];
  return mode === 'name'
    ? arr.sort(byName)
    : arr.sort((a, b) => (Number(b.totalQty) || 0) - (Number(a.totalQty) || 0) || byName(a, b));
}

/** Plain-text prep checklist ("2× Biryani\n…") the kitchen can copy/paste anywhere. */
export function prepListText(event, items, mode = 'name') {
  const sorted = sortItemsBy(items, mode);
  const total = sorted.reduce((s, r) => s + (Number(r.totalQty) || 0), 0);
  const title = (event && event.title) || 'Event';
  return [
    `Prep list — ${title}`,
    ...sorted.map((r) => `${r.totalQty}× ${r.name}`),
    `Total: ${total} item${total === 1 ? '' : 's'}`,
  ].join('\n');
}

/**
 * Best-effort parse of ONE free-text order into [{ name, qty }]. Splits on commas /
 * newlines / semicolons / "+", then pulls a quantity from common notations:
 *   trailing — "Biryani -2", "Naan x3", "Dosa (2)", "Tea *2", "qty: 4"
 *   leading  — "2 Samosa", "2x Samosa"
 * A trailing bare number is treated as part of the NAME (so "Chicken 65", "Tea 8oz"
 * survive), never as a quantity. Defaults to qty 1. Heuristic — for prep guidance.
 */
export function parseOrderItems(text) {
  const out = [];
  String(text || '')
    .split(/[\n,;]+|\s*\+\s*/)
    .forEach((frag) => {
      let s = frag.trim();
      if (!s) return;
      let qty = 1;
      let m = s.match(/^(.*\S)\s*(?:[-–x×*(]|qty:?)\s*(\d{1,3})\)?\s*$/i);       // trailing marker + number
      if (m) { qty = parseInt(m[2], 10) || 1; s = m[1]; }
      else {
        m = s.match(/^(\d{1,3})\s*[x×*]\s*(\D.*)$/i) || s.match(/^(\d{1,3})\s+(\D.*)$/i); // leading count
        if (m) { qty = parseInt(m[1], 10) || 1; s = m[2]; }
      }
      s = s.replace(/\s+/g, ' ').trim();
      if (s) out.push({ name: s, qty: qty > 0 ? qty : 1 });
    });
  return out;
}

/**
 * Aggregate parsed items across all free-text orders → [{ name, totalQty }],
 * busiest-first (same shape as aggregateItems, so it reuses sortItemsBy/prepListText).
 * Groups case-insensitively; the first-seen spelling is the display name.
 */
export function aggregateFreeText(orders) {
  const map = new Map();
  for (const o of orders || []) {
    for (const it of parseOrderItems(o && o.orderText)) {
      const key = it.name.toLowerCase();
      const prev = map.get(key) || { name: it.name, totalQty: 0 };
      prev.totalQty += it.qty;
      map.set(key, prev);
    }
  }
  return [...map.values()]
    .filter((x) => x.totalQty > 0)
    .sort((a, b) => b.totalQty - a.totalQty || String(a.name).localeCompare(String(b.name)));
}

/** Orders that are free text (menu-link events) rather than counted items, A→Z by name. */
export function freeTextOrders(orders) {
  return (orders || [])
    .filter((o) => String(o && o.orderText ? o.orderText : '').trim())
    .slice()
    .sort((a, b) => String(a.respondentName || '').localeCompare(String(b.respondentName || ''), undefined, { sensitivity: 'base' }));
}

/** Top-line counts for the responses header. */
export function summarize(orders) {
  const list = orders || [];
  const totalItems = list.reduce(
    (s, o) => s + (o.items || []).reduce((ss, it) => ss + (Number(it.qty) || 0), 0),
    0,
  );
  return { totalOrders: list.length, totalItems };
}

/** Estimated revenue across orders, from the prices stored on each order item. */
export function ordersRevenue(orders) {
  return (orders || []).reduce(
    (s, o) => s + (o.items || []).reduce((ss, it) => ss + (Number(it.qty) || 0) * (Number(it.price) || 0), 0),
    0,
  );
}

// ── CSV export ───────────────────────────────────────────────────────────────

function csvEscape(val) {
  const s = val == null ? '' : String(val);
  return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}

/** One row per order; one column per item + per custom field. */
export function ordersToCsv(event, orders) {
  const items = event.menuItems || [];
  const fields = (event.fields || []).filter((f) => fieldIsInput(f.type));
  const cols = [
    'Submitted', 'Name', 'Phone', 'Email',
    ...items.map((i) => i.name || 'Item'),
    'Order',
    ...fields.map((f) => f.label || 'Field'),
    'Instructions',
  ];
  const rows = (orders || []).map((o) => {
    const itemQty = items.map((i) => {
      const found = (o.items || []).find((it) => it.id === i.id || it.name === i.name);
      return found ? found.qty : '';
    });
    const answers = fields.map((f) => {
      const v = o.answers ? o.answers[f.id] : '';
      return Array.isArray(v) ? v.join('; ') : v ?? '';
    });
    return [
      formatDateTime(o.submittedAt),
      o.respondentName || '',
      o.respondentPhone || '',
      o.respondentEmail || '',
      ...itemQty,
      o.orderText || '',
      ...answers,
      o.instructions || '',
    ];
  });
  return [cols, ...rows].map((r) => r.map(csvEscape).join(',')).join('\n');
}

/** Trigger a browser download of a CSV string. */
export function downloadCsv(filename, csv) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Activity feed (derived audit log across events + orders) ──────────────────

/** Icon + label for a feed entry (order events, then lifecycle via HISTORY_META). */
export function activityMeta(entry) {
  if (entry.kind === 'order_placed') return { icon: '🧾', label: 'Order placed' };
  if (entry.kind === 'order_edited') return { icon: '✏️', label: 'Order edited' };
  return HISTORY_META[entry.action] || { icon: '•', label: entry.action };
}

/**
 * Merge event lifecycle history + order placements + order edits into one
 * newest-first audit stream. Purely derived — no separate storage.
 */
export function buildActivityFeed(events, orders) {
  const feed = [];
  for (const ev of events || []) {
    for (const h of ev.history || []) {
      feed.push({
        id: `${ev.id}-${h.at}-${h.action}`,
        at: h.at || 0,
        kind: 'lifecycle',
        action: h.action,
        eventId: ev.id,
        eventTitle: ev.title || 'Untitled event',
        by: h.by || '',
        summary: '',
      });
    }
  }
  for (const o of orders || []) {
    const at = toMs(o.submittedAt);
    if (at) {
      feed.push({
        id: `${o.id}-placed`,
        at,
        kind: 'order_placed',
        action: 'placed',
        eventId: o.eventId,
        eventTitle: o.eventTitle || '',
        by: o.respondentName || '',
        summary: '',
      });
    }
    for (const h of o.history || []) {
      feed.push({
        id: `${o.id}-${h.at}`,
        at: h.at || 0,
        kind: 'order_edited',
        action: 'edited',
        eventId: o.eventId,
        eventTitle: o.eventTitle || '',
        by: h.by || o.respondentName || '',
        summary: h.summary || '',
      });
    }
  }
  return feed.sort((a, b) => (b.at || 0) - (a.at || 0));
}

/** CSV of an activity feed (audit best practice: exportable). */
export function activityToCsv(feed) {
  const cols = ['When', 'Type', 'Event', 'By', 'Details'];
  const rows = (feed || []).map((e) => [
    formatDateTime(e.at), activityMeta(e).label, e.eventTitle || '', e.by || '', e.summary || '',
  ]);
  return [cols, ...rows].map((r) => r.map(csvEscape).join(',')).join('\n');
}

// ── Sharing + per-device order tracking (localStorage) ───────────────────────

export function shareLink(eventId) {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}/event/${eventId}`;
}

const DEVICE_KEY = 'event_device_id';
const ORDERS_KEY = 'event_orders_v1';

/** Stable random per-device id for soft one-order-per-person dedupe. */
export function getDeviceId() {
  try {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) {
      id = genId(16);
      localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch {
    return genId(16);
  }
}

function readLocalOrders() {
  try { return JSON.parse(localStorage.getItem(ORDERS_KEY) || '{}'); } catch { return {}; }
}

/** The order this device placed for an event (id + a snapshot to prefill edits). */
export function getLocalOrder(eventId) {
  const m = readLocalOrders();
  return m[eventId] || null;
}

/** Remember (or overwrite) this device's order for an event, so it can be edited. */
export function saveLocalOrder(eventId, snapshot) {
  try {
    const m = readLocalOrders();
    m[eventId] = snapshot;
    localStorage.setItem(ORDERS_KEY, JSON.stringify(m));
  } catch {
    /* ignore quota / private-mode errors */
  }
}

export function hasOrdered(eventId) {
  return !!getLocalOrder(eventId);
}
